import { removeHtmlTags } from 'utils/helpers'
import { htmlToText } from 'html-to-text'

const OPTIONS_HTML_TO_TEXT = {
  preserveNewlines: false,
  wordwrap: false,
  tags: {
    'p': { options: { leadingLineBreaks: 1, trailingLineBreaks: 1 } },
    'h1': { options: { leadingLineBreaks: 1, trailingLineBreaks: 1, uppercase: false } },
    'h2': { options: { leadingLineBreaks: 1, trailingLineBreaks: 1, uppercase: false } },
    'h3': { options: { leadingLineBreaks: 1, trailingLineBreaks: 1, uppercase: false } },
    'h4': { options: { leadingLineBreaks: 1, trailingLineBreaks: 1, uppercase: false } },
    'h5': { options: { leadingLineBreaks: 1, trailingLineBreaks: 1, uppercase: false } },
    'h6': { options: { leadingLineBreaks: 1, trailingLineBreaks: 1, uppercase: false } },
    'pre': { format: 'paragraph', options: { leadingLineBreaks: 1, trailingLineBreaks: 1 } }
  }
}

/**
 * Clean paste text
 * @param {string} html
 *
 * @returns {string}
 */
export function getPasteText(html = '') {
  if (typeof html !== 'string') return ''

  const data = removeHtmlTags(htmlToText(html, OPTIONS_HTML_TO_TEXT)).replace(/^\n+|\n+$/g, '')

  return `<p>${data.trim().split('\n').join('</p><p>')}</p>`.replace(/<p><\/p><p><\/p>|<p><\/p>/g, '<p><br></p>')
}

/**
 * Jodit get basic config
 * @param {object} params
 * @param {number} params.minHeight
 * @param {number} params.maxHeight
 * @param {boolean} params.readonly
 * @param {string} params.channel
 * @param {string} [params.editorCssClass]
 * @param {string} [params.locale]
 * @param {string} [params.enter]
 * @param {boolean} [params.isSmall]
 * @param {string} [params.placeholder]
 * @param {boolean} [params.displayAddVars]
 *
 * @returns {object}
 */
export function joditConfig(params) {
  const {
    minHeight, maxHeight, readonly,
    editorCssClass, channel, isSmall,
    locale, enter, placeholder, displayAddVars
  } = params

  return {
    toolbar: true,
    showWordsCounter: false,
    showCharsCounter: false,
    showXPathInStatusbar: false,
    toolbarButtonSize: 'small',
    toolbarAdaptive: false,
    useSplitMode: false,
    defaultMode: "1",
    colorPickerDefaultTab: 'color',
    askBeforePasteFromWord: false,
    askBeforePasteHTML: false,
    defaultActionOnPaste: 'insert_only_text',
    disablePlugins: "add-new-line",
    spellcheck: true,
    editorCssClass: editorCssClass || '',
    readonly: readonly,
    disabled: readonly,
    minHeight: minHeight,
    maxHeight: maxHeight,
    buttons: getbuttons(channel, isSmall, { displayAddVars }),
    buttonsMD: getbuttons(channel, isSmall, { displayAddVars }),
    buttonsSM: getbuttons(channel, isSmall, { displayAddVars }),
    buttonsXS: getbuttons(channel, isSmall, { displayAddVars }),
    placeholder: placeholder || '',
    language: locale,
    enter: enter || 'BR'
  }
}

export function joditPopupEvent() {
  return {
    a: markAsAtomic([
      {
        name: 'eye',
        tooltip: 'Open link',
        exec: (editor, current) => {
          if (current) {
            editor.ow.open(current)
          }
        }
      },
      {
        name: 'link',
        tooltip: 'Edit link',
        icon: 'pencil'
      },
      'unlink',
    ])
  }
}

/**
 * Get buttons by channel
 * @param {string} channel
 * @param {boolean} [isSmall] Only for chatmail
 * @param {boolean} [displayAddVars]
 * @returns
 */
function getbuttons(channel, isSmall, options) {
  const { displayAddVars = false } = options

  switch (channel) {
    case 'telegram':
      return ['bold', 'italic', 'link']
    case 'whatsapp':
    case 'whatsapp_whatsapp':
      return [
        'bold',
        'italic',
        ...displayAddVars ? ['add_vars'] : []
      ]
    case 'facebook':
    case 'instagram':
    case 'facebook_post':
    case 'instagram_post':
      return [
        ...displayAddVars ? ['add_vars'] : []
      ]
    case 'sms':
      return [
        ...displayAddVars ? ['add_vars'] : []
      ]
    case 'wall':
    case 'android':
    case 'ios':
    case 'chatweb':
      return [
        'bold',
        'italic',
        'underline',
        'strikethrough',
        '|',
        'paragraph',
        'ul',
        ...displayAddVars ? ['add_vars'] : []
      ]
    case 'chatmail':
      if (!isSmall)
        return [
          'bold',
          'italic',
          'underline',
          'strikethrough',
          '|',
          'fontsize',
          'brush',
          'paragraph',
          'align',
          'ul',
          '|',
          'image',
          'source',
          ...displayAddVars ? ['add_vars'] : []
        ]
      else
        return [
          'bold',
          'italic',
          'underline',
          'strikethrough',
          '|',
          'fontsize',
          'brush',
          'align',
          'ul',
          ...displayAddVars ? ['add_vars'] : []
        ]
    case 'inchat':
    case 'inChat':
      return [
        'bold',
        'italic',
        'underline',
        'strikethrough',
        '|',
        'brush',
        'paragraph',
        'align',
        'ul',
      ]
    default:
      return [
        'bold',
        'italic',
        ...displayAddVars ? ['add_vars'] : []
      ]
  }
}

/**
 * Mark atomic for link in editor
 * @param {object} obj
 *
 * @returns {object}
 */
function markAsAtomic(obj) {
  Object.defineProperty(obj, 'isAtom', {
    enumerable: false,
    value: true,
    configurable: false
  })

  return obj
}
